import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/queryClient';
import { 
  Activity, Cpu, HardDrive, MemoryStick, Globe, Database,
  Clock, Users, TrendingUp, TrendingDown, AlertTriangle,
  RefreshCw, Zap, Server, Wifi, CheckCircle, XCircle
} from 'lucide-react';

interface SystemMetrics {
  cpu: {
    usage: number;
    cores: number;
    temperature?: number;
  };
  memory: {
    used: number;
    total: number;
    usage: number;
  };
  disk: {
    used: number;
    total: number;
    usage: number;
  };
  network: {
    inbound: number;
    outbound: number;
    connections: number;
  };
  database: {
    connectionPool: number;
    activeQueries: number;
    avgResponseTime: number;
    cacheHitRate: number;
  };
  api: {
    requestsPerMinute: number;
    avgResponseTime: number;
    errorRate: number;
    activeConnections: number;
  };
  uptime: number;
  timestamp: string;
}

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  checks: Array<{
    name: string;
    status: 'pass' | 'warn' | 'fail';
    message: string;
    value?: number;
    threshold?: number;
  }>;
}

interface PerformanceAlert {
  id: string;
  type: 'warning' | 'critical' | 'info';
  metric: string;
  message: string;
  value: number;
  threshold: number;
  timestamp: string;
}

export function SystemPerformanceMonitor() {
  const [isRealTime, setIsRealTime] = useState(false);
  
  // Real-time system metrics
  const { data: metrics, isLoading: metricsLoading } = useQuery<SystemMetrics>({
    queryKey: ['system-metrics'],
    queryFn: () => apiRequest('/api/admin/system/metrics'),
    refetchInterval: isRealTime ? 5000 : false,
    refetchIntervalInBackground: false
  });

  // System health checks
  const { data: health, isLoading: healthLoading } = useQuery<SystemHealth>({
    queryKey: ['system-health'],
    queryFn: () => apiRequest('/api/admin/system/health'),
    refetchInterval: isRealTime ? 10000 : false,
    refetchIntervalInBackground: false
  });

  // Performance alerts
  const { data: alerts } = useQuery<PerformanceAlert[]>({
    queryKey: ['system-alerts'],
    queryFn: () => apiRequest('/api/admin/system/alerts'),
    refetchInterval: isRealTime ? 15000 : false,
    refetchIntervalInBackground: false
  });

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': case 'pass': return 'bg-green-100 text-green-800';
      case 'warning': case 'warn': return 'bg-yellow-100 text-yellow-800';
      case 'critical': case 'fail': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': case 'pass': return <CheckCircle className="w-4 h-4" />;
      case 'warning': case 'warn': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': case 'fail': return <XCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getProgressColor = (usage: number) => {
    if (usage >= 90) return 'bg-red-500';
    if (usage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (metricsLoading || healthLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with real-time toggle */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">System Performance</h2>
        <div className="flex items-center gap-4">
          <Button
            variant={isRealTime ? "default" : "outline"}
            size="sm"
            onClick={() => setIsRealTime(!isRealTime)}
          >
            <Activity className="w-4 h-4 mr-2" />
            {isRealTime ? 'Live Monitoring' : 'Static View'}
          </Button>
          <Badge className={getStatusColor(health?.status || 'unknown')}>
            {getStatusIcon(health?.status || 'unknown')}
            <span className="ml-1 capitalize">{health?.status || 'Unknown'}</span>
          </Badge>
        </div>
      </div>

      {/* System Resource Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* CPU Usage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              CPU Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold">{metrics?.cpu.usage.toFixed(1)}%</span>
                <Badge variant="outline">{metrics?.cpu.cores} cores</Badge>
              </div>
              <Progress 
                value={metrics?.cpu.usage || 0} 
                className="h-2"
                style={{ 
                  '--progress-foreground': getProgressColor(metrics?.cpu.usage || 0) 
                } as React.CSSProperties}
              />
              {metrics?.cpu.temperature && (
                <p className="text-xs text-gray-600">Temp: {metrics.cpu.temperature}°C</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Memory Usage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MemoryStick className="w-4 h-4" />
              Memory
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold">{metrics?.memory.usage.toFixed(1)}%</span>
                <span className="text-xs text-gray-600">
                  {formatBytes(metrics?.memory.used || 0)} / {formatBytes(metrics?.memory.total || 0)}
                </span>
              </div>
              <Progress 
                value={metrics?.memory.usage || 0} 
                className="h-2"
                style={{ 
                  '--progress-foreground': getProgressColor(metrics?.memory.usage || 0) 
                } as React.CSSProperties}
              />
            </div>
          </CardContent>
        </Card>

        {/* Disk Usage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              Disk Space
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold">{metrics?.disk.usage.toFixed(1)}%</span>
                <span className="text-xs text-gray-600">
                  {formatBytes(metrics?.disk.used || 0)} / {formatBytes(metrics?.disk.total || 0)}
                </span>
              </div>
              <Progress 
                value={metrics?.disk.usage || 0} 
                className="h-2"
                style={{ 
                  '--progress-foreground': getProgressColor(metrics?.disk.usage || 0) 
                } as React.CSSProperties}
              />
            </div>
          </CardContent>
        </Card>

        {/* Network Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Wifi className="w-4 h-4" />
              Network
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">In:</span>
                  <span>{formatBytes(metrics?.network.inbound || 0)}/s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Out:</span>
                  <span>{formatBytes(metrics?.network.outbound || 0)}/s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Connections:</span>
                  <span>{metrics?.network.connections || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Database & API Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Database Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Database Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{metrics?.database.connectionPool || 0}</p>
                  <p className="text-xs text-gray-600">Pool Connections</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{metrics?.database.activeQueries || 0}</p>
                  <p className="text-xs text-gray-600">Active Queries</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg Response Time:</span>
                  <span className="text-sm font-medium">{metrics?.database.avgResponseTime.toFixed(1)}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cache Hit Rate:</span>
                  <span className="text-sm font-medium">{metrics?.database.cacheHitRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* API Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              API Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{metrics?.api.requestsPerMinute || 0}</p>
                  <p className="text-xs text-gray-600">Requests/min</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{metrics?.api.activeConnections || 0}</p>
                  <p className="text-xs text-gray-600">Active Connections</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg Response Time:</span>
                  <span className="text-sm font-medium">{metrics?.api.avgResponseTime.toFixed(1)}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Error Rate:</span>
                  <span className="text-sm font-medium">{metrics?.api.errorRate.toFixed(2)}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Checks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            System Health Checks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {health?.checks.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  {getStatusIcon(check.status)}
                  <div>
                    <p className="font-medium text-sm">{check.name}</p>
                    <p className="text-xs text-gray-600">{check.message}</p>
                  </div>
                </div>
                <Badge className={getStatusColor(check.status)}>
                  {check.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Alerts */}
      {alerts && alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Performance Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                    alert.type === 'critical' ? 'text-red-500' : 
                    alert.type === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-sm">{alert.metric}</p>
                      <Badge className={getStatusColor(alert.type === 'critical' ? 'fail' : 'warn')}>
                        {alert.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{alert.message}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Current: {alert.value}</span>
                      <span>Threshold: {alert.threshold}</span>
                      <span>{new Date(alert.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Uptime */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">{formatUptime(metrics?.uptime || 0)}</p>
              <p className="text-sm text-gray-600">System Uptime</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">{metrics ? new Date(metrics.timestamp).toLocaleTimeString() : '--'}</p>
              <p className="text-sm text-gray-600">Last Updated</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">{health?.checks.filter(c => c.status === 'pass').length || 0}/{health?.checks.length || 0}</p>
              <p className="text-sm text-gray-600">Health Checks Passing</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}